/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import {
  LogLevel,
  RedirectRequest,
  Configuration,
  IPublicClientApplication,
  PublicClientApplication,
} from '@azure/msal-browser';
import {appConfig} from './configLoader';

const configVal = appConfig;
export const basename = document.querySelector('base')?.getAttribute('href') || '/';

const b2cFlows = {
  signUp: configVal.b2c.flows.signUp,
  signIn: configVal.b2c.flows.signIn,
};

let authorities = Object.entries(b2cFlows).reduce((final, item) => {
  const [b2cFlowName, b2cFlow] = item;
  return {
    ...final,
    [b2cFlowName]: {
      authority: `https://${configVal.b2c.tenantName}.b2clogin.com/${configVal.b2c.tenantName}.onmicrosoft.com/${b2cFlow}`,
    },
  };
}, {}) as {
  [b2cFlowName: string]: {
    authority: string;
  };
};

/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
export const b2cPolicies = {
  names: {
    ...b2cFlows,
  },
  authorities: authorities,
  authorityDomain: `${configVal.b2c.tenantName}.b2clogin.com`,
};

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalSignUpConfig: Configuration = {
  auth: {
    clientId: configVal.b2c.clientId, // This is the ONLY mandatory field that you need to supply.
    authority: b2cPolicies.authorities.signUp.authority, // Use a sign-up/sign-in user-flow as a default authority
    knownAuthorities: [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
    redirectUri: configVal.b2c.redirectUrn, // Points to window.location.origin. You must register this URI on Azure Portal/App Registration.
    postLogoutRedirectUri: basename, // Indicates the page to navigate after logout.
    navigateToLoginRequestUrl: false, // If "true", will navigate back to the original request location before processing the auth code response.
  },
  cache: {
    cacheLocation: 'sessionStorage', // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback: (level: any, message: any, containsPii: any) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          // case LogLevel.Info:
          //   console.info(message);
          //   return;
          // case LogLevel.Verbose:
          //   console.debug(message);
          //   return;
          case LogLevel.Warning:
            console.warn(message);
            return;
        }
      },
    },
  },
};

/**
 * MSAL should be instantiated outside of the component tree to prevent it from being re-instantiated on re-renders.
 * For more, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */

export const msalSignInConfig: Configuration = {
  ...msalSignUpConfig,
  auth: {
    ...msalSignUpConfig.auth,
    authority: b2cPolicies.authorities.signIn.authority,
    postLogoutRedirectUri: basename,
  },
};
/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest: RedirectRequest = {
  scopes: [
    `https://${configVal.b2c.tenantName}.onmicrosoft.com/${configVal.b2c.scopes.accountApiUrn}`,
  ],
};

/**
 * Logs a user out
 * @param instance msal instance
 * @param redirectPath path to redirect to
 * @default redirectPath = "{basename}"
 */
export const beginLogout = (
  instance: IPublicClientApplication,
  redirectPath?: string
) => {
  if (redirectPath) {
    return instance.logoutRedirect({postLogoutRedirectUri: redirectPath});
  } else {
    return instance.logoutRedirect();
  }
};

export const msalSignUpInstance = new PublicClientApplication(msalSignUpConfig);
export const msalSignInInstance = new PublicClientApplication(msalSignInConfig);
